import React, { FC, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { TooltipComponent } from "../../../components/Component";
import { Button } from "reactstrap";
import FormSelect from "../../form-components/SelectComponent";
import { Badge, DatePicker, Drawer, Progress, Table, Tooltip, Space, Button as AntButton, Dropdown, message, Segmented, Pagination, Empty, Skeleton } from "antd";
import type { MenuProps } from "antd";
import {
  SearchOutlined,
  EyeOutlined,
  FileTextOutlined,
  HistoryOutlined,
  NodeIndexOutlined,
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
  InboxOutlined,
  ArrowLeftOutlined,
  FilterOutlined,
  PlusOutlined,
  ReloadOutlined,
  BarsOutlined,
  TableOutlined,
} from "@ant-design/icons";
import Swal from "sweetalert2";
import SearchInput from "../../form-components/SearchComponent";
import SkeletonTable, {
  SkeletonTableColumnsType,
} from "../../../utils/skeleton-templates/table";
import PhaseCreation from "./PhaseCreation";
import FormMultiSelect from "../../form-components/MultiSelectComponent";
import { useDispatch } from "react-redux";
import { updatePhaseInDetailModalSlice } from "../../../store/slices/projectManagement/phase-view/PhaseInDetailView";
import { phaseApis } from "../../../api/Phase";
import { ColumnType } from "antd/es/table";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { updateMenuData } from "../../../store/slices/misc/menuData";
import TopMenu from "../../../route/topMenu";
import { RootState } from "../../../store/store";
import ProjectPageShell from "../shared/ProjectPageShell";
import { GenericStatusPill } from "../shared/statusColors";
import WbsCodeToggle from "../shared/WbsCodeToggle";
import { useWbsRecordName } from "../shared/useWbsCodes";
import { getDirtyFieldsOnly } from "../../../utils/use-form/GetDirtyFieldsOnly";
import { projectsApis } from "../../../api/projects";
import {
  AddAccess,
  ViewAccess,
} from "../../../components/accessControl/AccessControl";
import PhaseFieldDetailsUpdate from "./PhaseFieldDetailsUpdate";
import DocumentsTabWrapper from "../projectDocument/DocumentsTabWrapper";
import PhaseStatusTimeLine from "./PhaseStatusTimeLine";
import PhaseActivityStreamLine from "./PhaseActivityStreamLine";
import PhaseQuickViewCard from "./PhaseQuickViewCard";

const { RangePicker } = DatePicker;

const PHASE_VIEW_STORAGE_KEY = "pms.phases.viewMode";

const initialsOf = (name?: string) => {
  if (!name) return "—";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase();
};

const fmtListDate = (d?: string) => (d ? dayjs(d).format("DD MMM YYYY") : "—");

export interface PhaseTagsInterface {
  tagId: number;
  tagName: string;
  phaseTagId: number;
}
export type PhaseStatus =
  | "Active"
  | "Inactive"
  | "On Hold"
  | "Closed"
  | "Archived"
  | "Deleted"
  | "In Progress"
  | "Draft"
  | "Pending Approval"
  | "Approval Rejected";

export type PhaseFlagEnum = "Internal" | "External";

export interface PhasesListViewProps {
  phaseId: Number;
  phaseName: string;
  phaseOwnerId: number;
  phaseOwnerName: string; // not included in api
  projectId: number;
  phaseStartDate: string;
  phaseEndDate: string;
  flag: PhaseFlagEnum;
  status: PhaseStatus;
  phasesCompletionPercentage: string; // ex "15.5"
  activityCompletionPercentage: string; // ex "15.5"
  tasksCompletionPercentage: string; // ex "15.5"
  totalActivities: Number;
  completedActivities: Number;
  totalTasks: Number;
  completedTasks: Number;
  archivedAt: string; // ex "Fri May 23 2025 07:50:51 GMT+0000 (Coordinated Universal Time)"
  createdAt: string; // ex "Fri May 23 2025 07:50:51 GMT+0000 (Coordinated Universal Time)"
  archivedBy: Number;
  createdBy: Number;
  createdByUserName: string; // not included in api
  updatedBy: Number;
  phaseTags: PhaseTagsInterface[];
}

const PhasesListView: FC<{}> = ({}) => {
  const dispatch = useDispatch();
  const { data: SideNavCollapse } = useSelector(
    (state: any) => state.SideNavCollapseStatus
  );
  const { data: RootPageRenderState } = useSelector(
    (state: RootState) => state.RootPageRenderState
  );
  const { data: currentUser } = useSelector((state: any) => state.currentUser);
  const { data: PhaseInDetail } = useSelector(
    (state: RootState) => state.PhaseInDetailModal
  );
  const { allowEdit, allowAdd } = PhaseInDetail || {};
  const location = useLocation();
  const navigate = useNavigate();
  const {
    id: stateProjectId,
    projectName: stateProjectName,
    extra,
    filterPhaseId,
    filterPhaseName,
  } = location.state || {};
  const { screenName } = extra || {};
  const [highlightedPhaseId, setHighlightedPhaseId] = useState<number | null>(
    null
  );

  // Get projectId from Redux layout state as fallback
  const { currentProjectId, currentProjectName } = useSelector(
    (state: RootState) => state.layout
  );

  // Use Redux projectId as fallback when location state doesn't have it
  const projectId = stateProjectId || currentProjectId;
  const projectName = stateProjectName || currentProjectName;

  const [filtersModal, setFiltersModal] = useState(false);
  const [customDateRange, setCustomDateRange] = useState<any>();
  const [ownerFilterData, setOwnerFilterData] = useState([]);
  const [statusFilterData, setStatusFilterData] = useState([]);
  const [tagFilterData, setTagFilterData] = useState([]);
  const [filteredData, setFilteredData] = useState<any>({});

  // Drawer state for phase quick actions
  const [actionDrawer, setActionDrawer] = useState<{
    open: boolean;
    type: "view" | "documents" | "timeline" | "activityStream" | null;
    phase: PhasesListViewProps | null;
  }>({
    open: false,
    type: null,
    phase: null,
  });
  const [drawerViewMode, setDrawerViewMode] = useState<"view" | "edit">("view");
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const openActionDrawer = (
    type: "view" | "documents" | "timeline" | "activityStream",
    phase: PhasesListViewProps
  ) => {
    // Update Redux state for child components that need phase context
    dispatch(
      updatePhaseInDetailModalSlice({
        open: true,
        id: phase.phaseId,
        phaseName: phase.phaseName,
        activeTab: type,
        allowEdit: allowEdit,
        allowAdd: allowAdd,
        projectResponse: {
          ...PhaseInDetail?.projectResponse,
          id: projectId,
          name: projectName,
        },
        extra: {
          status: phase.status,
          startDate: phase.phaseStartDate,
          endDate: phase.phaseEndDate,
          ownerName: phase.phaseOwnerName,
          createdAt: phase.createdAt,
          createdByUserName: phase.createdByUserName,
        },
      })
    );
    setDrawerViewMode("view");
    setActionDrawer({ open: true, type, phase });
  };

  const closeActionDrawer = () => {
    setActionDrawer({ open: false, type: null, phase: null });
    setDrawerViewMode("view");
  };

  // Check if current phase can be edited
  const canEditCurrentPhase = () => {
    const phase = actionDrawer.phase;
    if (!phase || !allowEdit) return false;
    const status = phase.status?.toLowerCase();
    if (["closed", "archived", "deleted", "onhold", "cancelled"].includes(status)) return false;
    return true;
  };

  // Check if current phase can be archived
  const canArchiveCurrentPhase = () => {
    const phase = actionDrawer.phase;
    if (!phase || !allowEdit) return false;
    const status = phase.status?.toLowerCase();
    if (["archived", "deleted"].includes(status)) return false;
    return true;
  };

  // Check if current phase can be deleted
  const canDeleteCurrentPhase = () => {
    const phase = actionDrawer.phase;
    if (!phase || !allowEdit) return false;
    const status = phase.status?.toLowerCase();
    if (status === "deleted") return false;
    return true;
  };

  const handleArchivePhase = async () => {
    const phase = actionDrawer.phase;
    if (!phase) return;

    Swal.fire({
      title: "Are you sure?",
      text: "You want to archive this phase?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, archive it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setActionLoading("archive");
        try {
          const response = await phaseApis.archivePhase(Number(phase.phaseId));
          if (response?.status) {
            message.success("Phase archived successfully");
            refreshListView();
            closeActionDrawer();
          } else {
            message.error(response?.message || "Failed to archive phase");
          }
        } catch (error) {
          message.error("Failed to archive phase");
        } finally {
          setActionLoading(null);
        }
      }
    });
  };

  const handleDeletePhase = async () => {
    const phase = actionDrawer.phase;
    if (!phase) return;

    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setActionLoading("delete");
        try {
          const response = await phaseApis.deletePhase(Number(phase.phaseId));
          if (response?.status) {
            message.success("Phase deleted successfully");
            refreshListView();
            closeActionDrawer();
          } else {
            message.error(response?.message || "Failed to delete phase");
          }
        } catch (error) {
          message.error("Failed to delete phase");
        } finally {
          setActionLoading(null);
        }
      }
    });
  };

  // Hamburger menu items for view drawer
  const getDrawerMenuItems = (): MenuProps["items"] => {
    const items: MenuProps["items"] = [];

    if (canEditCurrentPhase()) {
      items.push({
        key: "edit",
        icon: <EditOutlined />,
        label: "Edit",
        onClick: () => setDrawerViewMode("edit"),
      });
    }

    if (canArchiveCurrentPhase()) {
      items.push({
        key: "archive",
        icon: <InboxOutlined />,
        label: "Archive",
        onClick: handleArchivePhase,
      });
    }

    if (canDeleteCurrentPhase()) {
      items.push({
        key: "delete",
        icon: <DeleteOutlined />,
        label: "Delete",
        danger: true,
        onClick: handleDeletePhase,
      });
    }

    return items;
  };

  const getDrawerTitle = () => {
    const { type, phase } = actionDrawer;
    if (!phase) return "";
    const name = phaseLabel(phase);
    switch (type) {
      case "view":
        return `${name} - Details`;
      case "documents":
        return `${name} - Documents`;
      case "timeline":
        return `${name} - Status Timeline`;
      case "activityStream":
        return `${name} - Activity Stream`;
      default:
        return name;
    }
  };

  const getDrawerContent = () => {
    const { type, phase } = actionDrawer;

    // For view type, check if we're in edit mode
    if (type === "view") {
      if (drawerViewMode === "edit") {
        return (
          <div style={{ padding: "16px 24px" }}>
            <PhaseFieldDetailsUpdate
              initialMode="edit"
              embedded={true}
              onModeChange={(mode) => {
                if (mode === "view") {
                  setDrawerViewMode("view");
                }
              }}
              onClose={closeActionDrawer}
              onRefresh={refreshListView}
            />
          </div>
        );
      }
      return (
        <PhaseQuickViewCard
          phaseId={phase?.phaseId as number | undefined}
          derivedProjectStatus={screenName}
          onClose={closeActionDrawer}
          onRefresh={refreshListView}
        />
      );
    }

    switch (type) {
      case "documents":
        return <DocumentsTabWrapper linkType="phase" parentDrawerMode={true} />;
      case "timeline":
        return <PhaseStatusTimeLine />;
      case "activityStream":
        return <PhaseActivityStreamLine activeTab="activityStream" />;
      default:
        return null;
    }
  };
  const renderActionButtons = (record: PhasesListViewProps) => (
    <Space size={8}>
      <Tooltip title="View Details">
        <AntButton
          type="text"
          className="pms-action-btn"
          icon={<EyeOutlined />}
          onClick={() => openActionDrawer("view", record)}
        />
      </Tooltip>
      <Tooltip title="Documents">
        <AntButton
          type="text"
          className="pms-action-btn"
          icon={<FileTextOutlined />}
          onClick={() => openActionDrawer("documents", record)}
        />
      </Tooltip>
      <Tooltip title="Status Timeline">
        <AntButton
          type="text"
          className="pms-action-btn"
          icon={<HistoryOutlined />}
          onClick={() => openActionDrawer("timeline", record)}
        />
      </Tooltip>
      <Tooltip title="Activity Stream">
        <AntButton
          type="text"
          className="pms-action-btn"
          icon={<NodeIndexOutlined />}
          onClick={() => openActionDrawer("activityStream", record)}
        />
      </Tooltip>
    </Space>
  );

  const columns: ColumnType<PhasesListViewProps>[] = [
    {
      title: "Phase",
      dataIndex: "phaseName",
      key: "phaseName",
      width: 250,
      fixed: "left",
      render(text: any, record: PhasesListViewProps) {
        return (
          <>
            <span
              className="inlineTableCellLink"
              id={`phase-${record?.phaseId}`}
              onClick={() => {
                // Navigate to Activities page with phase filter
                navigate(`/pms/project/activities/${projectId}`, {
                  state: {
                    ...location.state,
                    projectId,
                    projectName,
                    filterPhaseId: record.phaseId,
                    filterPhaseName: text,
                  }
                });
              }}
            >
              {phaseLabel(record)}
            </span>
            <TooltipComponent
              iconClass="info-circle text-info"
              icon={""}
              id={`phase-${record?.phaseId}`}
              direction="right"
              text={"Click to view " + phaseLabel(record) + " activities"}
              containerClassName={"mt-1"}
            />
          </>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 200,
      render: (status: string) => <GenericStatusPill status={status} />,
    },
    {
      title: "Owner",
      dataIndex: "phaseOwnerName",
      key: "phaseOwnerName",
      width: 250,
      render: (text: string, record: PhasesListViewProps, index: any) => {
        return (
          <>
            <span id={`ownerName-tooltip-${index}`}>
              {text?.length > 25 ? `${text?.substring(0, 22)}... ` : text}
            </span>
            {text?.length > 25 && (
              <TooltipComponent
                iconClass="info-circle text-info"
                icon="info"
                id={`ownerName-tooltip-${index}`}
                direction="top"
                text={text}
                containerClassName={""}
              />
            )}
          </>
        );
      },
    },
    {
      title: "Planned Start Date",
      dataIndex: "phaseStartDate",
      key: "phaseStartDate",
      width: 200,
      render: (text: string) =>
        text ? dayjs(text).format("DD-MMM-YYYY") : "N/A",
    },
    {
      title: "Planned End Date",
      dataIndex: "phaseEndDate",
      key: "phaseEndDate",
      width: 200,
      render: (text: string) =>
        text ? dayjs(text).format("DD-MMM-YYYY") : "N/A",
    },
    {
      title: "Tasks",
      dataIndex: "activityCompletionPercentage",
      key: "activityCompletionPercentage",
      width: 200,
      render: (data: any, record: PhasesListViewProps) => {
        return (
          <Tooltip
            title={`Closed: ${record.completedTasks}, Open: ${record.totalTasks}`}
            placement="top"
          >
            <Progress
              strokeLinecap="butt"
              size={[186, 10]}
              percent={data}
              status="active"
              strokeColor={{
                from: "#87d068",
                to: "#87d068",
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      title: "Budget Progress",
      dataIndex: "budgetConsumption",
      key: "budgetConsumption",
      width: 200,
      render: (data: any, record: any) => {
        const consumed = Number(data) || 0;
        const planned = Number(record?.budgetPlanned) || 0;
        const fmt = (n: number) =>
          `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
        if (planned <= 0) return consumed > 0 ? fmt(consumed) : "N/A";
        const percent = Math.round((consumed / planned) * 100);
        return (
          <Tooltip
            title={`${fmt(consumed)} of ${fmt(planned)} (${percent}%)`}
            placement="top"
          >
            <Progress
              strokeLinecap="butt"
              size={[186, 10]}
              percent={Math.min(100, percent)}
              status={percent > 100 ? "exception" : "active"}
              strokeColor={
                percent > 100
                  ? "#ff4d4f"
                  : { from: "#1677ff", to: "#1677ff" }
              }
            />
          </Tooltip>
        );
      },
    },
    {
      title: "Tags",
      dataIndex: "phaseTags",
      key: "phaseTags",
      width: 200,
      render: (
        phaseTags: PhaseTagsInterface[],
        record: PhasesListViewProps,
        index: any
      ) => {
        const text = phaseTags
          ?.map((tag: PhaseTagsInterface) => tag.tagName)
          ?.join(", ");
        return (
          <>
            <span id={`phaseTags-tooltip-${index}`}>
              {text?.length > 25 ? `${text?.substring(0, 22)}... ` : text}
            </span>
            {text?.length > 25 && (
              <TooltipComponent
                iconClass="info-circle text-info"
                icon="info"
                id={`phaseTags-tooltip-${index}`}
                direction="top"
                text={text}
                containerClassName={""}
              />
            )}
          </>
        );
      },
    },

    {
      title: "Created By",
      dataIndex: "createdByUserName",
      key: "createdByUserName",
      width: 250,
      render: (text: string, record: PhasesListViewProps, index: any) => {
        return (
          <>
            <span id={`createdBy-tooltip-${index}`}>
              {text?.length > 25 ? `${text?.substring(0, 22)}... ` : text}
            </span>
            {text?.length > 25 && (
              <TooltipComponent
                iconClass="info-circle text-info"
                icon="info"
                id={`createdBy-tooltip-${index}`}
                direction="top"
                text={text}
                containerClassName={""}
              />
            )}
          </>
        );
      },
    },
    {
      title: "Created Date",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 200,
      render: (text: string) => dayjs(text).format("DD-MMM-YYYY"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 220,
      fixed: "right",
      render: (_: any, record: PhasesListViewProps) => renderActionButtons(record),
    },
  ];
  const methods = useForm({
    mode: "onChange",
  });
  const {
    formState: { errors, dirtyFields },
  } = methods;
  const [dataSource, setDataSource] = useState<any>([]);
  // Phase names embed a WBS code; the toolbar toggle switches between the
  // trimmed label and the raw stored name. Clash-aware across the visible list.
  const phaseLabel = useWbsRecordName(dataSource, "phaseId", "phaseName");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    defaultCurrent: 1,
    defaultPageSize: 15,
    showSizeChanger: true,
    pageSizeOptions: ["15", "20", "30", "40", "50"],
    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [screenSize, setScreenSize] = useState(0);
  const [searchQuery, setsearchQuery] = useState(null);
  const [filterCount, setFilterCount] = useState(0);
  const [endOfList, setEndOfList] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState<"table" | "list">(
    () =>
      (localStorage.getItem(PHASE_VIEW_STORAGE_KEY) as "table" | "list") ||
      "list"
  );

  const fetchData = async (params: any, append = false) => {
    setLoading(true);
    try {
      const {
        sortOrder,
        pageSize,
        direction,
        pageDiff,
        paginationId,
        searchQuery,
      } = params;

      const apiResponse: any = await phaseApis.listPhases({
        pageSize: pageSize,
        searchQuery,
        direction,
        sortOrder,
        pageDiff,
        paginationId,
        projectIds: projectId?.toString(),
        derivedProjectStatus: screenName,
        tagIds: filteredData?.tag?.join(",") || null,
        phaseStatus: filteredData?.status || null,
        phaseOwnerIds: filteredData?.resourceHead?.join(",") || null,
        startDate:
          (customDateRange && dayjs(customDateRange[0]).format("YYYY-MM-DD")) ||
          null,
        endDate:
          (customDateRange && dayjs(customDateRange[1]).format("YYYY-MM-DD")) ||
          null,
        completionPercentage: filteredData?.completion || null,
      });
      const { data, status } = apiResponse || {};
      if (status) {
        const { list, count, projectResponse } = data || {};
        let _allowEdit = true;
        const {
          status: projectStatus,
          isLocked,
          lockedBy,
        } = projectResponse || {};
        if (
          ["closed", "onhold", "archived"]?.includes(
            projectStatus?.toLowerCase()
          )
        )
          // checking for project Status
          _allowEdit = false;
        if (isLocked && lockedBy != currentUser?.id) {
          _allowEdit = false;
        } // checking for locked project

        // Separate permission for Add Phase - less restrictive (anyone with module permission can add if conditions are met)
        let _allowAdd = true;
        if (
          ["closed", "onhold", "archived"]?.includes(
            projectStatus?.toLowerCase()
          )
        ) {
          _allowAdd = false;
        }
        if (isLocked && lockedBy != currentUser?.id) {
          _allowAdd = false;
        }

        dispatch(
          updatePhaseInDetailModalSlice({
            projectResponse: {
              ...projectResponse,
              name: projectName,
            },
            allowEdit: _allowEdit,
            allowAdd: _allowAdd,
          })
        );

        if (list?.length < pageSize) {
          setEndOfList(true);
        }
        setPagination((prev) => ({
          ...prev,
          total: Number(count),
        }));
        if (list)
          setDataSource((prev: any) => (append ? [...prev, ...list] : list));
      }
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const tableOnChange = async (
    newPagination: any,
    filters: any,
    sorter: any
  ) => {
    const apiParams = {
      oldPage: pagination.current,
      current: newPagination.current?.toString(),
      sortOrder: "DESC",
      pageSize: pagination.pageSize,
      direction: "P",
      pageDiff: 0,
      paginationId: null,
      searchQuery,
    };

    if (sorter.order) {
      apiParams.sortOrder = sorter.order === "ascend" ? "ASC" : "DESC";
    } else if (newPagination.pageSize != pagination.pageSize) {
      // if page size is changed
      apiParams.pageSize = newPagination.pageSize;
      const _currentPage =
        Math.floor(
          ((Number(pagination.current) - 1) * Number(pagination.pageSize)) /
            Number(newPagination.pageSize)
        ) + 1; // calculating new current page based on new page size
      apiParams.current = _currentPage;
      apiParams.pageDiff = _currentPage - 1;
    } else {
      if (newPagination.current != pagination.current) {
        apiParams.direction =
          newPagination.current > pagination.current
            ? "F" // Forward
            : "B"; // Backward
        apiParams.paginationId =
          apiParams.direction == "F"
            ? dataSource[dataSource.length - 1]?.paginationId // Last item ID for backward direction
            : dataSource[0]?.paginationId; // First item ID for forward direction

        apiParams.pageDiff =
          Math.abs(newPagination.current - pagination.current) - 1;
      }
    }

    setPagination({
      ...pagination,
      current: apiParams.current,
      pageSize: apiParams.pageSize,
    });
    await fetchData({
      ...apiParams,
      // selectFilter1,
      // selectFilter2,
      // selectFilter3,
    });
  };

  const refreshListView = () => {
    try {
      setEndOfList(false);
      setPagination({
        current: 1,
        pageSize: 15,
        defaultCurrent: 1,
        defaultPageSize: 15,
        showSizeChanger: true,
        pageSizeOptions: ["15", "20", "30", "40", "50"],
        total: 0,
      });
      refreshData({
        pageDiff: 0,
      }); // calling for initial data load , if the view type is table
    } catch (error) {}
  };

  useEffect(() => {
    refreshListView();
  }, [RootPageRenderState]);

  useEffect(() => {
    dispatch(
      updateMenuData(
        TopMenu?.projectNav?.map((item: any) => {
          return {
            ...item,
            linkState: location.state,
          };
        })
      )
    );
  }, [location]);

  const fetchTagFilterData = async () => {
    try {
      const response = await phaseApis.getTagsFilterData(projectId?.toString());
      const { data, status } = response || {};
      if (status) {
        const options = data.map((item: any) => {
          return {
            label: item.tagName,
            value: item.tagId,
          };
        });
        setTagFilterData(options);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  const fetchOwnerFilterData = async () => {
    try {
      const response = await phaseApis.getOwnersFilterData(
        projectId?.toString()
      );
      const { data, status } = response || {};
      if (status) {
        const options = data.map((item: any) => {
          return {
            label: item.userName,
            value: item.userId,
          };
        });
        setOwnerFilterData(options);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  const fetchStatusFilterData = async () => {
    try {
      const response = await projectsApis.fetchStatusFiltersByModule("Phase");
      const { data, status } = response;
      if (status) {
        const options = data.map((item: any) => {
          return {
            label: item,
            value: item,
          };
        });
        setStatusFilterData(options);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };
  useEffect(() => {
    if (filtersModal) {
      fetchTagFilterData();
      fetchOwnerFilterData();
      fetchStatusFilterData();
    }
  }, [filtersModal]);

  // Highlight + scroll to a specific phase when navigated back from the
  // activities list with filterPhaseId in location.state.
  useEffect(() => {
    if (!filterPhaseId || !dataSource?.length) return;
    setHighlightedPhaseId(Number(filterPhaseId));
    // Defer scroll until row paints
    const t = window.setTimeout(() => {
      const el = document.querySelector(".highlighted-phase-row");
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
    // Auto-fade highlight so it stops drawing the eye after the user has seen it
    const clear = window.setTimeout(() => setHighlightedPhaseId(null), 4000);
    return () => {
      window.clearTimeout(t);
      window.clearTimeout(clear);
    };
  }, [filterPhaseId, dataSource]);
  useEffect(() => {
    if (Object?.keys(filteredData)?.length > 0) {
      refreshListView();
    }
  }, [filteredData]);

  const refreshData = (overRideData?: any) => {
    fetchData({
      oldPage: 1,
      current: pagination.current.toString(),
      sortOrder: "DESC",
      pageSize: pagination.pageSize.toString(),
      direction: "P",
      pageDiff: pagination.current - 1,
      paginationId: null,
      searchQuery,
      dateRange: "Quarter to Date",
      // customDateRange,
      ...overRideData,
    });
  };

  const viewChange = () => {
    setScreenSize(window.innerWidth);
  };

  useEffect(() => {
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
    };
  }, []);

  const onListPageChange = (page: number, pageSize: number) => {
    tableOnChange({ current: page, pageSize }, {}, {});
  };

  const renderPhaseList = () => {
    if (loading) {
      return (
        <div className="pms-list-rows">
          {Array.from({ length: 8 }, (_, i) => (
            <div key={i} className="pms-list-row" style={{ cursor: "default" }}>
              <Skeleton active paragraph={false} title={{ width: "80%" }} />
            </div>
          ))}
        </div>
      );
    }

    if (!dataSource?.length) {
      return (
        <div className="pms-hub-empty">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No phases"
          />
        </div>
      );
    }

    return (
      <>
        <div className="pms-list-rows pms-view-fade">
          {dataSource.map((record: PhasesListViewProps) => {
            const tasksPct = Number(record?.activityCompletionPercentage) || 0;
            const isHighlighted =
              highlightedPhaseId &&
              Number(record?.phaseId) === Number(highlightedPhaseId);
            return (
              <div
                key={(record as any).uid}
                className={`pms-list-row phase-list-row${
                  isHighlighted ? " highlighted-phase-row" : ""
                }`}
              >
                <div className="pms-row-main">
                  <div
                    className="pms-row-title inlineTableCellLink"
                    title={record.phaseName}
                    /* title keeps the raw name so the code stays recoverable */
                    onClick={() =>
                      navigate(`/pms/project/activities/${projectId}`, {
                        state: {
                          ...location.state,
                          projectId,
                          projectName,
                          filterPhaseId: record.phaseId,
                          filterPhaseName: record.phaseName,
                        },
                      })
                    }
                  >
                    {phaseLabel(record)}
                  </div>
                  <div style={{ marginTop: 6 }}>
                    <GenericStatusPill status={record.status} />
                  </div>
                </div>

                <div className="pms-row-people">
                  <span className="pms-avatar">
                    {initialsOf(record.phaseOwnerName)}
                  </span>
                  <span className="pms-meta-text">
                    {record.phaseOwnerName || "Unassigned"}
                  </span>
                </div>

                <div className="pms-row-progress">
                  <Tooltip
                    title={`Closed: ${record.completedTasks}, Open: ${record.totalTasks}`}
                    placement="top"
                  >
                    <Progress
                      strokeLinecap="butt"
                      size={[160, 8]}
                      percent={tasksPct}
                      status="active"
                      strokeColor={{ from: "#87d068", to: "#87d068" }}
                    />
                  </Tooltip>
                  <div
                    className="pms-figure-caption"
                    style={{
                      marginTop: 6,
                      textTransform: "none",
                      letterSpacing: 0,
                    }}
                  >
                    {String(record.completedTasks ?? 0)}/
                    {String(record.totalTasks ?? 0)} tasks
                  </div>
                </div>

                <div className="pms-row-dates">
                  <div>{fmtListDate(record.phaseStartDate)}</div>
                  <div>{fmtListDate(record.phaseEndDate)}</div>
                </div>

                <div
                  className="phase-row-actions"
                  onClick={(e) => e.stopPropagation()}
                >
                  {renderActionButtons(record)}
                </div>
              </div>
            );
          })}
        </div>
        <div className="pms-hub-pagination">
          <Pagination
            size="small"
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            showSizeChanger={pagination.showSizeChanger}
            pageSizeOptions={pagination.pageSizeOptions}
            onChange={onListPageChange}
            showTotal={(t: number) => `Total ${t} phases`}
          />
        </div>
      </>
    );
  };

  const toolbar = (
    <>
      <FormProvider {...methods}>
        <form>
          <div style={{ width: "300px" }}>
            <SearchInput
              name="search"
              placeholder="Search..."
              width="100%"
              prefixIcon={<SearchOutlined />}
              onDebouncedSearch={async (value: any) => {
                setsearchQuery(value);
                setPagination({
                  ...pagination,
                  current: 1,
                });
                await fetchData({
                  oldPage: 1,
                  current: 1,
                  sortOrder: "DESC",
                  pageSize: pagination.pageSize.toString(),
                  direction: "P",
                  pageDiff: 0,
                  paginationId: null,
                  searchQuery: value,
                  customDateRange,
                });
              }}
              debounceDelay={300}
            />
          </div>
        </form>
      </FormProvider>
      <span className="pms-toolbar-spacer" />
      <WbsCodeToggle />
      <AddAccess moduleName="Phases">
        <AntButton
          type="primary"
          icon={<PlusOutlined />}
          disabled={!allowAdd}
          onClick={() => {
            setModalOpen(true);
          }}
        >
          Add Phase
        </AntButton>
      </AddAccess>
      <ViewAccess moduleName="Phases">
        <AntButton
          type="text"
          icon={<ReloadOutlined />}
          title="Refresh"
          onClick={() => refreshListView()}
        />
      </ViewAccess>
      <Badge color="geekblue" size="small" count={filterCount}>
        <AntButton
          type="text"
          icon={<FilterOutlined />}
          onClick={() => setFiltersModal(true)}
        />
      </Badge>
      <Segmented
        value={viewMode}
        onChange={(v) => {
          const value = String(v) as "table" | "list";
          localStorage.setItem(PHASE_VIEW_STORAGE_KEY, value);
          setViewMode(value);
        }}
        options={[
          { value: "table", icon: <TableOutlined />, title: "Table" },
          { value: "list", icon: <BarsOutlined />, title: "List" },
        ]}
      />
    </>
  );

  return (
    <>
      <ProjectPageShell
        headTitle="Phases"
        breadcrumb={[{ label: projectName }, { label: "Phases" }]}
        onBack={() => navigate(-1)}
        toolbar={toolbar}
      >
        {viewMode === "table" ? (
          <div className="pms-table-panel">
            <SkeletonTable
              loading={loading}
              columns={columns as SkeletonTableColumnsType[]}
            >
              <Table
                rowKey="uid"
                className="pms-hub-table"
                columns={columns}
                dataSource={dataSource}
                pagination={pagination}
                onChange={tableOnChange}
                size="middle"
                scroll={{ x: "max-content" }}
                rowClassName={(record: any) =>
                  highlightedPhaseId &&
                  Number(record?.phaseId) === Number(highlightedPhaseId)
                    ? "highlighted-phase-row"
                    : ""
                }
              />
            </SkeletonTable>
          </div>
        ) : (
          renderPhaseList()
        )}
      </ProjectPageShell>

      <PhaseCreation
        projectId={projectId}
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        refreshData={refreshData}
      />

      <Drawer
        maskClosable={false}
        title={
          <div className="d-flex justify-content-between align-items-center">
            <strong>Filters</strong>
            <>
              <div style={{ textAlign: "right" }}>
                <Button
                  onClick={() => {
                    methods.reset();
                    setFilterCount(0);
                    setFilteredData({});
                    refreshListView();
                  }}
                  color=""
                  className="m-3 mt-0 mb-0 ps-4 pe-4"
                  size="sm"
                >
                  <span style={{ color: "#0772f8" }}>Reset</span>
                </Button>
              </div>
            </>
          </div>
        }
        placement="right"
        width={450}
        onClose={() => {
          setFiltersModal(false);
        }}
        open={filtersModal}
        styles={{ body: { paddingBottom: 80 } }}
      >
        <div className="d-flex align-items-center flex-wrap w-100">
          <FormProvider {...methods}>
            <form
              onSubmit={methods.handleSubmit((data) => {
                setFilteredData(data);
                setFiltersModal(false);
                setFilterCount(0);
                // Count how many fields were actually changed (dirty)
                const dirtyData = getDirtyFieldsOnly(data, dirtyFields);
                let count =
                  dirtyData !== undefined ? Object.keys(dirtyData).length : 0;
                if (
                  customDateRange &&
                  customDateRange[0] &&
                  customDateRange[1]
                ) {
                  count += 1;
                }
                setFilterCount(count);
                if (
                  count == 0 &&
                  typeof data == "object" &&
                  Object.keys(data).length == 0
                )
                  refreshListView();
              })}
              className="w-100"
            >
              <div key={"filters5"} className="mt-0 mb-2">
                <label className="form-label">Select Date Range</label>
                <RangePicker
                  name={`dateRange`}
                  value={
                    customDateRange
                      ? [
                          dayjs(customDateRange[0].toDate()),
                          dayjs(customDateRange[1].toDate()),
                        ]
                      : null
                  }
                  onChange={(dates) => {
                    if (dates && dates[0] && dates[1]) {
                      setCustomDateRange([dates[0], dates[1]]);
                    } else {
                      setCustomDateRange(null);
                    }
                  }}
                  style={{ width: "100%" }}
                />
              </div>
              <div key={"filters1"} className="mb-2 mt-0">
                <FormMultiSelect
                  name={`resourceHead`}
                  label={"Resource Head"}
                  placeholder={"By Resource Head"}
                  options={ownerFilterData}
                />
              </div>

              <div key={"filters2"} className="mt-0 mb-2">
                <FormMultiSelect
                  name={`tag`}
                  label={"Tag"}
                  placeholder={"By Tag"}
                  options={tagFilterData}
                />
              </div>

              <div key={"filters3"} className="mt-0 mb-2">
                <FormSelect
                  name={`completion`}
                  label={"Completion %"}
                  placeholder={"By Completion %"}
                  options={Array.from({ length: 20 }, (_, i) => {
                    const start = i * 5 + 1;
                    const end = (i + 1) * 5;
                    return {
                      value: `${start}-${end}`,
                      label: `${start}–${end}%`,
                    };
                  })}
                />
              </div>

              <div key={"filters4"} className="mt-0 mb-2">
                <FormSelect
                  name={`status`}
                  label={"Status"}
                  placeholder={"By Status"}
                  options={statusFilterData}
                />
              </div>

              <div className="actions clearfix">
                <ul>
                  <li>
                    <Button color="primary" size="sm" type="submit">
                      Find
                    </Button>
                  </li>
                  <li>
                    <Button
                      size="sm"
                      color="primary"
                      className="btn-dim btn btn-secondary"
                      type="button"
                      onClick={() => {
                        methods.reset();
                        setFiltersModal(false);
                        setFilteredData({});
                        setFilterCount(0);
                      }}
                    >
                      Cancel
                    </Button>
                  </li>
                </ul>
              </div>
            </form>
          </FormProvider>
        </div>
      </Drawer>

      {/* Action Drawer for View/Documents/Timeline/ActivityStream */}
      <Drawer
        title={
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {/* {actionDrawer.type === "view" && drawerViewMode === "edit" && (
                <EditOutlined style={{ color: "#1890ff" }} />
              )}
              {actionDrawer.type === "view" && drawerViewMode === "view" && (
                <EyeOutlined style={{ color: "#1890ff" }} />
              )} */}
              {actionDrawer.type === "documents" && <FileTextOutlined style={{ color: "#2557d6" }} />}
              {actionDrawer.type === "timeline" && <HistoryOutlined style={{ color: "#2557d6" }} />}
              {actionDrawer.type === "activityStream" && <NodeIndexOutlined style={{ color: "#2557d6" }} />}
              <span style={{ fontWeight: 600 }}>
                {drawerViewMode === "edit" && actionDrawer.type === "view"
                  ? `${phaseLabel(actionDrawer.phase)} - Edit`
                  : getDrawerTitle()}
              </span>
            </div>
            {/* Back to View button when in edit mode */}
            {actionDrawer.type === "view" && drawerViewMode === "edit" && (
              <AntButton
                icon={<ArrowLeftOutlined />}
                onClick={() => setDrawerViewMode("view")}
                style={{
                  borderRadius: 6,
                  background: "#f5f5f5",
                  border: "1px solid #d9d9d9",
                  color: "#333",
                }}
              >
                Back to View
              </AntButton>
            )}
            {/* Hamburger menu for view type only when in view mode */}
            {actionDrawer.type === "view" && drawerViewMode === "view" && (getDrawerMenuItems()?.length ?? 0) > 0 && (
              <Dropdown
                menu={{ items: getDrawerMenuItems() }}
                trigger={["click"]}
                placement="bottomRight"
              >
                <AntButton
                  type="text"
                  icon={<MoreOutlined style={{ fontSize: 18 }} />}
                  style={{
                    borderRadius: 6,
                    padding: "4px 8px",
                  }}
                />
              </Dropdown>
            )}
          </div>
        }
        placement="right"
        width={720}
        onClose={closeActionDrawer}
        open={actionDrawer.open}
        styles={{ body: { padding: 0 } }}
        destroyOnClose
      >
        <div style={{ padding: actionDrawer.type === "view" ? 0 : 16 }}>
          {getDrawerContent()}
        </div>
      </Drawer>
    </>
  );
};

export default PhasesListView;
